'use client';

import React, { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import {
  CalendarIcon,
  ChartPieIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  HomeIcon,
  UsersIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowUpTrayIcon,
  Cog8ToothIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  MapIcon,
  GlobeAmericasIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ComputerDesktopIcon,
   ClockIcon, SparklesIcon
} from '@heroicons/react/24/outline';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/ttechLogo.png';
import lightLogo from '../assets/appLogoFinalLight.png';
import darkLogo from '../assets/appLogoFinalDark.png';
import ttechLogoSquare from '../assets/ttechLogoSquare.png';
import { AlertBanner, AlertDemo } from '../components/TrialBanner';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'HQ Mode', href: '/measurementhq', icon: ComputerDesktopIcon },
  { name: 'Measurements', href: '/measurements', icon: MapIcon },
  { name: 'Projects', href: '/projects', icon: FolderIcon },
  { name: 'Upload', href: '/upload', icon: ArrowUpTrayIcon },
  { name: 'Settings', href: '/settings', icon: Cog8ToothIcon },
];

const teams = [
  { id: 1, name: 'Integrations', href: '/settings/Integrations', initial: 'I' },
  { id: 2, name: 'Team', href: '/settings/Team', initial: 'T' },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Layout({ children }) {
  const { user, loading: authLoading, isAuthenticated } = useContext(AuthContext);
  const [profile, setProfile] = useState({ first_name: null, last_name: null, is_email_confirmed: false });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved !== null ? JSON.parse(saved) : false;
    }
    return false;
  }); // Initialize with localStorage value to prevent flash
  const [currentPath, setCurrentPath] = useState('');
  const [isInOrganization, setIsInOrganization] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(null);

  // OTP flow state
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [otpError, setOtpError] = useState('');
  const [loadingAction, setLoadingAction] = useState('');

  const [hasUnmapped, setHasUnmapped] = useState(false);
  const [hoveredTooltip, setHoveredTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const navigate = useNavigate();
  const location = useLocation();
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  // Tooltip handlers
  const handleTooltipEnter = (e, content) => {
    if (!sidebarCollapsed) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.right + 8,
      y: rect.top + rect.height / 2
    });
    setHoveredTooltip(content);
    console.log('Tooltip triggered:', content); // Debug log
  };

  const handleTooltipLeave = () => {
    setHoveredTooltip(null);
    console.log('Tooltip hidden'); // Debug log
  };

  // Load collapse state from localStorage (this useEffect is now redundant but kept for safety)
  useEffect(() => {
    const savedCollapsedState = localStorage.getItem('sidebarCollapsed');
    if (savedCollapsedState !== null) {
      const saved = JSON.parse(savedCollapsedState);
      if (saved !== sidebarCollapsed) {
        setSidebarCollapsed(saved);
      }
    }
  }, []);

  // Save collapse state to localStorage
  const toggleSidebarCollapse = () => {
    const newCollapsedState = !sidebarCollapsed;
    setSidebarCollapsed(newCollapsedState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsedState));
  };

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      if (location.pathname !== '/') {
        navigate('/');
      }
    }
  }, [authLoading, isAuthenticated, navigate, location.pathname]);

  // Fetch profile and organization data only once or when explicitly needed
  useEffect(() => {
    if (authLoading || !isAuthenticated || !user || profileLoaded) return;

    const fetchProfileAndOrganization = async () => {
      try {
        // Fetch profile (including is_email_confirmed)
        const { data: profileData, error: profileError } = await supabase
          .from('profile')
          .select('first_name, last_name, is_email_confirmed')
          .eq('id', user.id)
          .single();

        if (profileError || !profileData) {
          console.error('Error fetching profile or profile not found:', profileError);
          navigate('/');
          return;
        }

        setProfile({
          first_name: profileData.first_name || 'Guest',
          last_name: profileData.last_name || '',
          is_email_confirmed: profileData.is_email_confirmed,
        });
        setEmailConfirmed(profileData.is_email_confirmed);
        setProfileLoaded(true);

        // Fetch organization membership
        const { data: orgData, error: orgError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (orgError || !orgData) {
          console.error('Error fetching organization or not in organization:', orgError);
          setIsInOrganization(false);
        } else {
          setIsInOrganization(true);
        }
      } catch (err) {
        console.error('Unexpected error fetching profile/organization:', err);
        navigate('/');
      }
    };

    fetchProfileAndOrganization();
    setCurrentPath(window.location.pathname);
  }, [user, authLoading, isAuthenticated, navigate, profileLoaded]);

  // Update current path without triggering fetch
  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  const closeSidebar = () => {
    if (sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  // Fetch unmapped measurements status in useEffect after profile is loaded
  useEffect(() => {
    const fetchUnmappedStatus = async () => {
      if (!user || !profileLoaded || !isInOrganization) return;
  
      try {
        // first get the org's member IDs
        const { data: orgData, error: orgError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        if (orgError || !orgData) return;
  
        const { data: membersData, error: membersError } = await supabase
          .from('organization_members')
          .select('user_id')
          .eq('organization_id', orgData.organization_id);
        if (membersError) return;
  
        const memberIds = membersData.map((m) => m.user_id);
  
        // query the view, but only fetch one row and ask for the total count
        const { data, count, error } = await supabase
          .from('measurements_without_project')
          .select('id', { count: 'exact' })
          .in('profile_id', memberIds)
          .range(0, 0);   // limits to the first row
  
        if (error) throw error;
  
        setHasUnmapped((count || 0) > 0);
      } catch (err) {
        console.error('Error checking unmapped measurements:', err);
      }
    };
  
    fetchUnmappedStatus();
  }, [user, profileLoaded, isInOrganization]);

  useEffect(() => {
  if (!user || !profileLoaded || !isInOrganization) return;

  const fetchSubscriptionInfo = async () => {
    try {
      // Get user's organization
      const { data: orgData, error: orgError } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (orgError || !orgData) return;

      // Get subscription info
      const { data: subData, error: subError } = await supabase
        .from('organization_subscriptions')
        .select('*')
        .eq('id', orgData.organization_id)
        .single();

      if (subError || !subData) return;

      setSubscriptionInfo({
        status: subData.subscription_status,
        tier: subData.subscription_tier,
        trialEndDate: subData.trial_end_date,
        companyName: subData.name,
        currentPeriodEnd: subData.current_period_end,
        currentMembers: subData.current_member_count,
        maxMembers: subData.max_member_count,
      });
      console.log('Subscription Info:', subscriptionInfo);
    } catch (err) {
      console.error('Error fetching subscription info:', err);
    }
  };

  fetchSubscriptionInfo();
}, [user, profileLoaded, isInOrganization]);
  
  const handleUpgrade = () => {
    navigate('/settings/Billing');
  };

  const handleManageMembers = () => {
    navigate('/settings/Team');
  };

  // --- OTP Confirmation Flow ---
const handleSendConfirmationEmail = async () => {
  try {
    setOtpError('');
    setOtpMessage('');
    setLoadingAction('send'); // Set loading state for sending email
    const { error } = await supabase.auth.signInWithOtp({
      email: user.email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) {
      console.error('Error sending OTP:', error.message);
      setOtpError(`Failed to send code: ${error.message}`);
    } else {
      setOtpMessage('We just sent a code to your email. Enter it below to confirm.');
      setOtpStep(true);
    }
  } catch (err) {
    console.error('Unexpected error sending OTP:', err.message);
    setOtpError('An unexpected error occurred. Please try again later.');
  } finally {
    setLoadingAction(''); // Clear loading state
  }
};

const handleVerifyOtp = async () => {
  try {
    setOtpError('');
    setOtpMessage('');
    setLoadingAction('verify'); // Set loading state for verifying OTP
    const { error } = await supabase.auth.verifyOtp({
      email: user.email,
      token: otp.trim(),
      type: 'email',
    });
    if (error) {
      console.error('OTP verification failed:', error.message);
      setOtpError(`Code verification failed: ${error.message}`);
    } else {
      setOtpMessage('Your email has been confirmed!');
      const { error: updateError } = await supabase
        .from('profile')
        .update({ is_email_confirmed: true })
        .eq('id', user.id);
      if (updateError) {
        console.error('Error updating email confirmation in profile:', updateError.message);
      }
      setEmailConfirmed(true);
      setOtp('');
      setOtpStep(false);
    }
  } catch (err) {
    console.error('Unexpected error verifying OTP:', err.message);
    setOtpError('An unexpected error occurred. Please try again.');
  } finally {
    setLoadingAction(''); // Clear loading state
  }
};
// --- End OTP Flow ---

  if (authLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  

  return (
    <div className="flex h-screen" onClick={closeSidebar}>
      {/* Mobile Header Section */}
      <div className="fixed top-0 left-0 right-0 z-10 flex h-12 items-center bg-gray-900 text-white md:hidden">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSidebarOpen(!sidebarOpen);
          }}
          className="ml-4"
        >
          {sidebarOpen ? (
            <XMarkIcon className="h-6 w-6" />
          ) : (
            <Bars3Icon className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Sidebar */}
      <div
        className={classNames(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-black text-white transition-all duration-300 ease-in-out',
          // Mobile behavior
          sidebarOpen ? 'translate-x-0 w-60' : '-translate-x-full w-60',
          // Desktop behavior
          'md:relative md:translate-x-0',
          // Desktop width based on collapsed state
          sidebarCollapsed ? 'md:w-16' : 'md:w-60'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={classNames(
          'flex h-16 items-center border-b border-gray-800 p-6 relative',
          sidebarCollapsed ? 'md:justify-center md:p-2' : 'justify-center'
        )}>
          {sidebarCollapsed ? (
            <div className="hidden md:block">
              <img alt="Your Company" src={ttechLogoSquare} className="h-8 w-8" />
            </div>
          ) : (
            <img alt="Your Company" src={darkLogo} className="w-auto" />
          )}
          {/* Mobile always shows full logo */}
          <div className="md:hidden">
            <img alt="Your Company" src={darkLogo} className="w-auto" />
          </div>
          
          {/* Desktop Collapse Toggle Button - moved to header divider */}
          <div className="hidden md:block">
            <button
              onClick={toggleSidebarCollapse}
              className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-[9999] flex h-6 w-6 items-center justify-center rounded-full bg-gray-800 text-gray-400 shadow-lg transition-colors hover:bg-gray-700 hover:text-white border border-gray-600"
            >
              {sidebarCollapsed ? (
                <ChevronRightIcon className="h-4 w-4" />
              ) : (
                <ChevronLeftIcon className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex flex-1 flex-col overflow-y-auto px-4 py-4">
          <ul className="space-y-6">
            <li>
              <ul className="space-y-1">
                {navigation.map((item) => {
                  const isDisabled =
                    (item.name === 'Projects' || item.name === 'Upload') && !isInOrganization;
                  return (
                    <li key={item.name} className="relative">
                      <Link
                        to={item.href}
                        className={classNames(
                          isDisabled
                            ? 'text-gray-500 cursor-not-allowed opacity-60'
                            : currentPath === item.href
                            ? 'bg-gray-800 text-white'
                            : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                          'flex gap-x-3 rounded-md p-2 text-sm font-semibold items-center relative',
                          sidebarCollapsed ? 'md:justify-center md:px-2' : ''
                        )}
                        onClick={(e) => isDisabled && e.preventDefault()}
                        onMouseEnter={(e) => handleTooltipEnter(e, {
                          text: item.name,
                          subtitle: isDisabled ? 'Join an Organization to Access' : null
                        })}
                        onMouseLeave={handleTooltipLeave}
                      >
                        <item.icon aria-hidden="true" className="h-6 w-6 shrink-0" />
                        <span className={classNames(
                          sidebarCollapsed ? 'md:hidden' : '',
                          'truncate'
                        )}>
                          {item.name}
                        </span>
                        {isDisabled && !sidebarCollapsed && (
                          <LockClosedIcon className="h-5 w-5 text-gray-500 ml-2 md:hidden" aria-hidden="true" />
                        )}
                      </Link>
                      
                      {/* Tooltip for non-collapsed disabled items */}
                      {isDisabled && !sidebarCollapsed && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap z-30">
                          Join an Organization to Access
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
            <li>
              <div className={classNames(
                'text-xs font-semibold text-gray-400',
                sidebarCollapsed ? 'md:hidden' : ''
              )}>
                Quick Action
              </div>
              <ul className="mt-2 space-y-1">
                {teams.map((team) => (
                  <li key={team.name} className="relative group">
                    <a
                      href={team.href}
                      onMouseEnter={(e) => handleTooltipEnter(e, { text: team.name })}
                      onMouseLeave={handleTooltipLeave}
                      className={classNames(
                        team.current
                          ? 'bg-gray-800 text-white'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                        'flex gap-x-3 rounded-md p-2 text-sm font-semibold items-center relative',
                        sidebarCollapsed ? 'md:justify-center md:px-2' : ''
                      )}
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-gray-700 bg-gray-800 text-[0.625rem] font-medium text-gray-400 group-hover:text-white">
                        {team.initial}
                      </span>
                      <span className={classNames(
                        'truncate',
                        sidebarCollapsed ? 'md:hidden' : ''
                      )}>
                        {team.name}
                      </span>
                    </a>
                    
                    {/* Tooltip for collapsed state */}
                    {sidebarCollapsed && (
                      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 hidden group-hover:block bg-gray-800 text-white text-sm rounded-md px-3 py-2 shadow-lg whitespace-nowrap z-30 border border-gray-600">
                        {team.name}
                      </div>
                    )}
                  </li>
                ))}
                {hasUnmapped && (
                  <li className="relative group">
                    <a
                      href="/unmappedproject"
                      onMouseEnter={(e) => handleTooltipEnter(e, { text: 'Unmapped Measurements' })}
                      onMouseLeave={handleTooltipLeave}
                      className={classNames(
                        'flex items-center gap-x-3 rounded-md p-2 text-sm font-semibold text-yellow-400 transition-all hover:bg-gray-800 relative',
                        sidebarCollapsed ? 'md:justify-center md:px-2' : ''
                      )}
                    >
                      <GlobeAmericasIcon
                        className="h-6 w-6 shrink-0 text-yellow-400 group-hover:text-yellow-300"
                        aria-hidden="true"
                      />
                      <span className={classNames(
                        sidebarCollapsed ? 'md:hidden' : ''
                      )}>
                        Unmapped
                      </span>
                    </a>
                    
                    {/* Tooltip for collapsed state */}
                    {sidebarCollapsed && (
                      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 hidden group-hover:block bg-gray-800 text-white text-sm rounded-md px-3 py-2 shadow-lg whitespace-nowrap z-30 border border-gray-600">
                        Unmapped
                      </div>
                    )}
                  </li>
                )}
              </ul>
            </li>
          </ul>
        </nav>

        {/* Footer */}
        <div className="border-t border-gray-800 p-4">
       
{user && emailConfirmed === false && !sidebarCollapsed && (
  <div className="mb-6 rounded-lg bg-gradient-to-r from-red-900/80 to-red-800/80 p-4 shadow-md transition-all duration-300 hover:shadow-lg">
    {!otpStep ? (
      <div className="space-y-3">
        <div className="flex items-center gap-x-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400 animate-pulse" />
          <p className="text-sm font-semibold text-red-200">
            Email Confirmation Required
          </p>
        </div>
        <p className="text-xs text-red-300 leading-relaxed">
          Please confirm your email to secure your account and unlock full access.
        </p>
        <button
          onClick={handleSendConfirmationEmail}
          disabled={loadingAction === 'send'}
          className="w-full rounded-md bg-red-600 py-2 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:ring-offset-black disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loadingAction === 'send' ? (
            <>
              <svg
                className="animate-spin h-5 w-5 mr-2 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
                </svg>
              Sending...
            </>
          ) : (
            'Send Confirmation Code'
          )}
        </button>
      </div>
    ) : (
      <div className="space-y-3">
        <p className="text-sm font-semibold text-red-200">
          Enter Your Confirmation Code
        </p>
        <p className="text-xs text-red-300 leading-relaxed">
          Check your email ({user.email}) for the code we sent.
        </p>
        <div className="space-y-2">
          <input
            type="text"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder="6-digit code"
            className="w-full rounded-md border border-gray-600 bg-gray-900 p-2 text-sm text-white placeholder-gray-500 focus:border-red-400 focus:ring-1 focus:ring-red-400 focus:outline-none transition-all duration-200"
            disabled={loadingAction === 'verify'}
          />
          <button
            onClick={handleVerifyOtp}
            disabled={loadingAction === 'verify' || !otp.trim()}
            className="w-full rounded-md bg-red-600 py-2 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:ring-offset-black disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loadingAction === 'verify' ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 mr-2 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
                </svg>
              Verifying...
            </>
          ) : (
            'Verify'
          )}
          </button>
        </div>
        <div className="text-xs text-red-300">
          <p>
            Not receiving the code?{' '}
            <button
              onClick={handleSendConfirmationEmail}
              className="underline text-red-200 hover:text-red-100 transition-colors duration-200"
              disabled={loadingAction === 'send'}
            >
              Resend it
            </button>{' '}
            or{' '}
            <a
              href="https://www.transtechconnect.com/whitelist"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-red-200 hover:text-red-100 transition-colors duration-200"
            >
              check our whitelisting guide
            </a>
          </p>
        </div>
        {otpError && (
          <p className="text-xs text-red-400 italic animate-fade-in">{otpError}</p>
        )}
        {otpMessage && (
          <p className="text-xs text-green-400 italic animate-fade-in">{otpMessage}</p>
        )}
      </div>
    )}
  </div>
)}

{/* Email confirmation indicator for collapsed state */}
{user && emailConfirmed === false && sidebarCollapsed && (
  <div className="hidden md:flex justify-center mb-4">
    <div 
      className="relative"
      onMouseEnter={(e) => handleTooltipEnter(e, { text: 'Email confirmation required' })}
      onMouseLeave={handleTooltipLeave}
    >
      <div className="h-8 w-8 rounded-full bg-red-600 flex items-center justify-center animate-pulse cursor-pointer">
        <ExclamationTriangleIcon className="h-5 w-5 text-white" />
      </div>
    </div>
  </div>
)}

          <div className={classNames(
            'flex items-center gap-x-4',
            sidebarCollapsed ? 'md:flex-col md:gap-y-2 md:items-center' : ''
          )}>
            <div className={classNames(
              sidebarCollapsed ? 'md:text-center' : ''
            )}>
              {profileLoaded ? (
                <p className={classNames(
                  'text-sm font-medium text-white',
                  sidebarCollapsed ? 'md:hidden' : ''
                )}>
                  {profile.first_name} {profile.last_name}
                </p>
              ) : (
                <p className={classNames(
                  'text-sm font-medium text-white',
                  sidebarCollapsed ? 'md:hidden' : ''
                )}>
                  Loading...
                </p>
              )}
              <a className={classNames(
                'text-xs text-gray-400',
                sidebarCollapsed ? 'md:hidden' : ''
              )} href="/settings/account">
                View profile
              </a>
              
              {/* User avatar for collapsed state */}
              {sidebarCollapsed && profileLoaded && (
                <div className="hidden md:block relative group">
                  <div className="h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {profile.first_name?.[0]}{profile.last_name?.[0]}
                    </span>
                  </div>
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 hidden group-hover:block bg-gray-800 text-white text-sm rounded-md px-3 py-2 shadow-lg whitespace-nowrap z-[9999] border border-gray-600">
                    {profile.first_name} {profile.last_name}
                    <div className="text-xs text-gray-400">View profile</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {user && (
            <button
              onClick={handleLogout}
              className={classNames(
                'mt-4 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 transition-colors',
                sidebarCollapsed ? 'md:w-10 md:h-10 md:p-0 md:flex md:items-center md:justify-center' : 'w-full'
              )}
              title={sidebarCollapsed ? 'Logout' : ''}
            >
              <span className={sidebarCollapsed ? 'md:hidden' : ''}>Logout</span>
              {sidebarCollapsed && (
                <svg className="hidden md:block h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-y-auto">
      <AlertBanner 
        subscriptionInfo={subscriptionInfo}
        onUpgrade={handleUpgrade}
        onManageMembers={handleManageMembers}
        onUpdatePayment={handleUpgrade}
      />


        <main className="flex-1 p-4 bg-gray-100 pt-20 md:pt-6 overflow-x-hidden">
          {children}
        </main>
      </div>

      {/* Portal for Tooltips */}
      {hoveredTooltip && typeof document !== 'undefined' && createPortal(
        <div 
          className="fixed bg-gray-900 text-white text-sm rounded-md px-3 py-2 shadow-xl whitespace-nowrap border border-gray-600 pointer-events-none z-[9999] transition-opacity duration-200"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translateY(-50%)'
          }}
        >
          {hoveredTooltip.text}
          {hoveredTooltip.subtitle && (
            <div className="text-xs text-gray-400 mt-1">
              {hoveredTooltip.subtitle}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}